import React, { useState, useEffect } from 'react';
import {
  Modal,
  Button,
  Select,
  message,
  Spin,
  Checkbox,
  Input,
  Pagination,
  Empty,
  Alert
} from 'antd';
import {
  SearchOutlined,
  LoadingOutlined,
  ExclamationCircleOutlined
} from '@ant-design/icons';
import {
  assign_brand_to_products,
  get_all_products_without_brand
} from '../../../../Controllers/Products/Products';
import { get_all_product_brands } from '../../../../Controllers/Products/Brands';
import toast from 'react-hot-toast';

const { Option } = Select;

const AssignBrandModal = ({
  open,
  onClose,
  selectedProductIds = [],
  onSuccess,
  productData = {},
  brand_Id
}) => {
  const [loading, setLoading] = useState(false);
  const [brands, setBrands] = useState([]);
  const [brandLoading, setBrandLoading] = useState(false);
  const [products, setProducts] = useState([]);
  const [productLoading, setProductLoading] = useState(false);
  const [selectedBrand, setSelectedBrand] = useState(null);
  const [selectedProducts, setSelectedProducts] = useState(selectedProductIds);
  const [searchText, setSearchText] = useState('');
  const [selectAllCurrentPage, setSelectAllCurrentPage] = useState(false);
  const [selectAllGlobal, setSelectAllGlobal] = useState(false);

  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalCount: 0,
    totalPages: 0,
    limit: 500
  });

  const [searchTimeout, setSearchTimeout] = useState(null);

  const fetchBrands = async () => {
    try {
      setBrandLoading(true);
      const result = await get_all_product_brands({
        status: 1,
        page: 1,
        limit: 1000
      });

      if (result?.status) {
        setBrands(result?.data?.data || []);
      } else {
        toast.error(result?.message || 'Failed to load brands');
      }
    } catch (error) {
      toast.error(error.message || 'Error loading brands');
    } finally {
      setBrandLoading(false);
    }
  };

  const fetchProducts = async (page = 1, search = '', isSearch = false) => {
    try {
      setProductLoading(true);

      const result = await get_all_products_without_brand({
        search: search,
        page: page,
        limit: pagination.limit
      });

      if (result?.status) {
        const productList = result?.data?.data || [];

        if (isSearch) {
          const existingProductIds = productList.map(p => p._id);
          setSelectedProducts(prev =>
            prev.filter(id => existingProductIds.includes(id))
          );
        }

        setProducts(productList);

        setPagination(prev => ({
          ...prev,
          currentPage: result?.data?.currentPage || page,
          totalCount: result?.data?.totalCount || 0,
          totalPages: result?.data?.totalPages || 0
        }));

        if (selectedProductIds.length > 0 && productData._id && page === 1) {
          setSelectedProducts(prev => {
            const newSelections = [...prev];
            selectedProductIds.forEach(id => {
              if (!newSelections.includes(id) && productList.some(p => p._id === id)) {
                newSelections.push(id);
              }
            });
            return newSelections;
          });
        }
      } else {
        toast.error(result?.message || 'Failed to load products');
      }
    } catch (error) {
      toast.error(error.message || 'Error loading products');
    } finally {
      setProductLoading(false);
    }
  };

  const handleSearch = (value) => {
    setSearchText(value);

    if (searchTimeout) {
      clearTimeout(searchTimeout);
    }

    const timeout = setTimeout(() => {
      fetchProducts(1, value, true);
    }, 500);

    setSearchTimeout(timeout);
  };

  const handlePageChange = (page) => {
    fetchProducts(page, searchText);
    setSelectAllCurrentPage(false);
  };

  const handleProductSelect = (productId) => {
    setSelectedProducts(prev => {
      if (prev.includes(productId)) {
        return prev.filter(id => id !== productId);
      } else {
        return [...prev, productId];
      }
    });
  };

  const handleSelectAllCurrentPage = () => {
    const currentPageProductIds = products.map(product => product._id);

    if (selectAllCurrentPage) {
      setSelectedProducts(prev =>
        prev.filter(id => !currentPageProductIds.includes(id))
      );
    } else {
      const newSelections = [...selectedProducts];
      currentPageProductIds.forEach(id => {
        if (!newSelections.includes(id)) {
          newSelections.push(id);
        }
      });
      setSelectedProducts(newSelections);
    }
    setSelectAllCurrentPage(!selectAllCurrentPage);
  };


  const handleSelectAllGlobal = async () => {
    if (selectAllGlobal) {
      setSelectedProducts([]);
      setSelectAllGlobal(false);
    } else {
      try {
        setLoading(true);
        const result = await get_all_products_without_brand({
          search: searchText,
          page: 1,
          limit: 10000
        });

        if (result?.status) {
          const allProductIds = result.data.data.map(p => p._id);
          setSelectedProducts(allProductIds);
          setSelectAllGlobal(true);
          toast.success(`Selected ${allProductIds.length} products`);
        }
      } catch (error) {
        toast.error('Failed to select all products');
      } finally {
        setLoading(false);
      }
    }
  };

  const handleAssignBrand = async () => {
    if (!selectedBrand) {
      toast.error('Please select a brand');
      return;
    }

    if (selectedProducts.length === 0) {
      toast.error('Please select at least one product');
      return;
    }

    try {
      setLoading(true);

      const data = {
        brand_id: selectedBrand,
        product_ids: selectedProducts
      };

      const result = await assign_brand_to_products(data);

      if (result?.status) {
        toast.success(result.message || 'Brand assigned successfully');
        if (onSuccess) {
          onSuccess();
        }
        onClose();
      } else {
        toast.error(result?.message || 'Failed to assign brand');
      }
    } catch (error) {
      toast.error(error.message || 'Error assigning brand');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) {
      fetchBrands();
      fetchProducts(1);

      if (brand_Id) {
        setSelectedBrand(brand_Id);
      } else if (productData?.brand?._id) {
        setSelectedBrand(productData.brand._id);
      }

    } else {
      setSelectedProducts(selectedProductIds);
      setSearchText('');
      setSelectAllCurrentPage(false);
      setSelectAllGlobal(false);
      setPagination({
        currentPage: 1,
        totalCount: 0,
        totalPages: 0,
        limit: 50
      });
      setSelectedBrand(null);
    }
  }, [open]);

  useEffect(() => {
    if (products.length > 0) {
      const currentPageProductIds = products.map(p => p._id);
      const allSelectedOnPage = currentPageProductIds.every(id =>
        selectedProducts.includes(id)
      );
      setSelectAllCurrentPage(allSelectedOnPage);

      if (selectedProducts.length === pagination.totalCount && pagination.totalCount > 0) {
        setSelectAllGlobal(true);
      } else {
        setSelectAllGlobal(false);
      }
    } else {
      setSelectAllCurrentPage(false);
      setSelectAllGlobal(false);
    }
  }, [selectedProducts, products, pagination.totalCount]);

  return (
    <Modal
      title="Assign Brand to Products"
      open={open}
      onCancel={onClose}
      width={900}
      footer={[
        <Button key="close" onClick={onClose}>
          Cancel
        </Button>,
        <Button
          key="submit"
          type="primary"
          loading={loading}
          onClick={handleAssignBrand}
          disabled={!selectedBrand || selectedProducts.length === 0}
          className="!bg-[#07b553] !border-[#07b553] hover:!bg-[#38cb82] hover:!border-[#38cb82] text-white"
        >
          Assign Brand ({selectedProducts.length.toLocaleString()} products)
        </Button>,
      ]}
    >
      <div className="space-y-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Select Brand *
          </label>
          <Select
            placeholder="Search and select brand"
            value={selectedBrand}
            onChange={setSelectedBrand}
            style={{ width: '100%' }}
            showSearch
            allowClear
            loading={brandLoading}
            filterOption={(input, option) =>
              option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
            }
          >
            {brands.map(brand => (
              <Option key={brand._id} value={brand._id}>
                {brand.name}
              </Option>
            ))}
          </Select>
        </div>

        <div>
          <div className="flex justify-between items-center mb-2">
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Select Products
                <span className="ml-2 text-sm font-normal text-gray-500">
                  ({selectedProducts.length.toLocaleString()} selected of {pagination.totalCount.toLocaleString()} total)
                </span>
              </label>
            </div>
            <div className="flex items-center space-x-4">
              <div className="flex items-center">
                <Checkbox
                  checked={selectAllCurrentPage}
                  onChange={handleSelectAllCurrentPage}
                  disabled={products.length === 0}
                >
                  <span className="text-sm">Select Page ({products.length})</span>
                </Checkbox>
              </div>
              <div className="flex items-center">
                <Checkbox
                  checked={selectAllGlobal}
                  onChange={handleSelectAllGlobal}
                  disabled={pagination.totalCount === 0}
                >
                  <span className="text-sm">Select All ({pagination.totalCount.toLocaleString()})</span>
                </Checkbox>
              </div>
            </div>
          </div>

          <div className="mb-4">
            <Input
              placeholder="Search products by name or SKU..."
              prefix={<SearchOutlined />}
              value={searchText}
              onChange={(e) => handleSearch(e.target.value)}
              allowClear
              size="large"
              className="!border-[#21c45d] hover:!border-[#21c45d] focus:!border-[#21c45d]"
            />
          </div>

          <div className="border rounded-lg">
            <div className="flex justify-between items-center p-3 bg-gray-50 border-b">
              <div className="text-sm text-gray-600">
                Showing {(pagination.currentPage - 1) * pagination.limit + 1}-
                {Math.min(pagination.currentPage * pagination.limit, pagination.totalCount)}
                of {pagination.totalCount.toLocaleString()} products
              </div>
              {pagination.totalPages > 1 && (
                <div className="flex items-center">
                  <Pagination
                    current={pagination.currentPage}
                    total={pagination.totalCount}
                    pageSize={pagination.limit}
                    onChange={handlePageChange}
                    showSizeChanger={false}
                    showQuickJumper
                    size="small"
                    showTotal={(total, range) => `${range[0]}-${range[1]} of ${total} items`}
                  />
                </div>
              )}
            </div>

            <div className="max-h-96 overflow-y-auto">
              {productLoading ? (
                <div className="p-8 text-center">
                  <Spin
                    indicator={<LoadingOutlined style={{ fontSize: 24, color: '#21c45d' }} spin />}
                    size="large"
                  />
                  <p className="text-gray-500 mt-4">Loading products...</p>
                </div>
              ) : products.length === 0 ? (
                <div className="p-8 text-center">
                  <Empty
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                    description={
                      searchText
                        ? `No products found matching "${searchText}"`
                        : "No products found without brand"
                    }
                  />
                </div>
              ) : (
                <div className="divide-y">
                  {products.map(product => (
                    <div
                      key={product._id}
                      className={`p-3 hover:bg-gray-50 cursor-pointer flex items-center ${selectedProducts.includes(product._id) ? 'bg-green-50 border-l-4 border-[#21c45d]' : ''
                        }`}
                      onClick={() => handleProductSelect(product._id)}
                    >
                      <Checkbox
                        checked={selectedProducts.includes(product._id)}
                        onChange={() => handleProductSelect(product._id)}
                        className="mr-4"
                      />
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-gray-900 truncate">
                          {product.name}
                        </div>
                        <div className="text-sm text-gray-500 mt-1">
                          <span className="mr-4">SKU: {product.ref || 'N/A'}</span>
                          <span className="mr-4">Stock: {product.stock?.toFixed?.(2) || 0}</span>
                          {product.category && (
                            <span>Category: {product.category.name}</span>
                          )}
                        </div>
                      </div>
                      {product.brand && (
                        <div className="text-xs text-gray-600 bg-gray-100 px-2 py-1 rounded">
                          Has brand
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {pagination.totalPages > 1 && (
              <div className="flex justify-between items-center p-3 bg-gray-50 border-t">
                <div className="text-sm text-gray-600">
                  Page {pagination.currentPage} of {pagination.totalPages}
                </div>
                <div className="flex space-x-2">
                  <Button
                    size="small"
                    type='primary'
                    onClick={() => handlePageChange(pagination.currentPage - 1)}
                    disabled={pagination.currentPage === 1}
                    className="!bg-[#07b553] !border-[#07b553] hover:!bg-[#38cb82] hover:!border-[#38cb82] text-white"
                  >
                    Previous
                  </Button>
                  <Button
                    size="small"
                    type='primary'
                    onClick={() => handlePageChange(pagination.currentPage + 1)}
                    disabled={pagination.currentPage === pagination.totalPages}
                    className="!bg-[#07b553] !border-[#07b553] hover:!bg-[#38cb82] hover:!border-[#38cb82] text-white"
                  >
                    Next
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>

        {selectedProducts.length > 0 && (
          <div className="bg-green-50 p-4 rounded-lg border border-green-200">
            <div className="flex justify-between items-center">
              <div>
                <h4 className="font-medium text-green-800 mb-1">Summary</h4>
                <p className="text-green-700">
                  <strong>{selectedProducts.length.toLocaleString()}</strong> product(s) selected
                </p>
                {pagination.totalCount > 0 && (
                  <p className="text-sm text-green-600 mt-1">
                    ({((selectedProducts.length / pagination.totalCount) * 100).toFixed(1)}% of all products without brand)
                  </p>
                )}
              </div>
              {selectedProducts.length > 500 && (
                <div className="text-orange-600 bg-orange-50 px-3 py-1 rounded">
                  <strong>Note:</strong> Bulk operation in progress
                </div>
              )}
            </div>
          </div>
        )}

        {selectedProducts.length > 1000 && (
          <div className="bg-yellow-50 border border-yellow-200 p-3 rounded-lg">
            <div className="flex items-start">
              <ExclamationCircleOutlined className="text-yellow-500 mr-2 mt-0.5" />
              <div className="text-yellow-800">
                <p className="font-medium">Large Selection Detected</p>
                <p className="text-sm">
                  You have selected {selectedProducts.length.toLocaleString()} products.
                  This operation may take a few moments to complete.
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
};

export default AssignBrandModal;